import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  listCopyKeys, resolveCopy, copyHistory, saveCopyOverride, revertCopyOverride,
  type CopyKey, type CopyResolution, type CopyVersion, type CopyScopeType,
} from '../../lib/copyAdmin';
import { listOrganizations, listVenueDirectory, type Organization, type DirectoryVenue }
  from '../../lib/tenancy';
import { listCompliance, type ComplianceEvent } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { useAdminScope } from '../../admin/scope';
import './AdminPages.css';

/**
 * The copy editor.
 *
 * ── THREE VALUES, NEVER ONE ──────────────────────────────────────────────────────────
 *
 * Every key shows what the code says, what this scope overrides it with, and what
 * actually renders. Collapsing those into a single box is how somebody edits "the
 * default" without realising they are writing a tenant override, or reverts something
 * believing it will restore text that was never there.
 *
 * ── THE SCOPE IS ALWAYS STATED, NEVER INFERRED ───────────────────────────────────────
 *
 * The Admin header's organization context seeds the scope selector once, as a
 * convenience. It does not silently become the thing being edited: the selector shows the
 * level explicitly, and the save button says which one it will write to. Editing another
 * tenant's wording by accident because a header happened to be set is exactly the mistake
 * this arrangement exists to prevent.
 *
 * ── THIS IS TEXT MANAGEMENT, NOT A PAGE BUILDER ──────────────────────────────────────
 *
 * No layout, no sections, no ordering, no visibility, no HTML. Values are plain text and
 * render as text.
 */

type ScopeTarget = { type: CopyScopeType; id: string | null };

function ScopePicker({
  supported, value, onChange, orgs, venues, events,
}: {
  supported: CopyScopeType[];
  value: ScopeTarget;
  onChange: (next: ScopeTarget) => void;
  orgs: Organization[];
  venues: DirectoryVenue[];
  events: ComplianceEvent[];
}) {
  const options = value.type === 'organization'
    ? orgs.map((o) => ({ id: o.id, label: o.name }))
    : value.type === 'venue'
      ? venues.map((v) => ({ id: v.venueId, label: v.name }))
      : value.type === 'event'
        ? events.map((e) => ({ id: e.eventId, label: e.title }))
        : [];

  return (
    <div className="cpy-scope">
      <label className="field cpy-scope__field">
        <span className="field__label">Editing at</span>
        <select
          className="field__input"
          value={value.type}
          onChange={(e) => onChange({ type: e.target.value as CopyScopeType, id: null })}
        >
          {/* Only the scopes this key declares. A platform-only key cannot be edited per
              venue, and the control does not pretend otherwise. */}
          {supported.map((s) => (
            <option key={s} value={s}>
              {s === 'platform' ? 'Platform (everyone)' : s[0].toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </label>

      {value.type !== 'platform' && (
        <label className="field cpy-scope__field">
          <span className="field__label">{value.type}</span>
          <select
            className="field__input"
            value={value.id ?? ''}
            onChange={(e) => onChange({ type: value.type, id: e.target.value || null })}
          >
            <option value="">Choose one…</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </label>
      )}
    </div>
  );
}

function History({ rows }: { rows: CopyVersion[] }) {
  if (rows.length === 0) return <p className="adm-lede">No versions at this scope yet.</p>;
  return (
    <ol className="cpy-history">
      {rows.map((v) => (
        <li key={v.id} className={`cpy-history__row${v.isCurrent ? ' is-current' : ''}`}>
          <div className="cpy-history__meta">
            <span className="badge badge--muted">v{v.version}</span>
            <span className={`badge ${v.isCurrent ? 'badge--ok' : 'badge--muted'}`}>{v.state}</span>
            <span className="cpy-history__who">
              {v.createdBy ?? 'unknown'} · {new Date(v.createdAt).toLocaleString()}
            </span>
          </div>
          {/* Read-only. Restoring a previous wording is a deliberate edit, not a click
              inside a log. */}
          <p className="cpy-history__value">{v.value}</p>
        </li>
      ))}
    </ol>
  );
}

function Editor({
  meta, scope, onScopeChange, orgs, venues, events, onSaved,
}: {
  meta: CopyKey;
  scope: ScopeTarget;
  onScopeChange: (next: ScopeTarget) => void;
  orgs: Organization[];
  venues: DirectoryVenue[];
  events: ComplianceEvent[];
  onSaved: () => void;
}) {
  const [resolution, setResolution] = useState<CopyResolution | null>(null);
  const [history, setHistory] = useState<CopyVersion[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const scopeReady = scope.type === 'platform' || Boolean(scope.id);

  const load = useCallback(() => {
    if (!scopeReady) { setResolution(null); setHistory([]); return; }
    setError(null);
    resolveCopy(meta.key, scope.type, scope.id)
      .then((r) => { setResolution(r); setDraft(r.override?.value ?? r.effective); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load this key.'));
    copyHistory(meta.key, scope.type, scope.id).then(setHistory).catch(() => setHistory([]));
  }, [meta.key, scope.type, scope.id, scopeReady]);

  useEffect(load, [load]);

  async function save() {
    if (!resolution) return;
    setBusy(true);
    setError(null);
    try {
      await saveCopyOverride({
        key: meta.key, scopeType: scope.type, scopeId: scope.id, value: draft,
        // The version this editor was looking at. If somebody else saved in the meantime
        // the engine refuses and we reload rather than overwrite them.
        expectedVersion: resolution.override?.version ?? null,
      });
      load();
      onSaved();
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Someone else changed this key while you had it open. Reloaded their version.');
        load();
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not save.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function revert() {
    if (!resolution?.override) return;
    if (!window.confirm(
      `Remove the ${scope.type} override for "${meta.key}"? The next level down takes over.`,
    )) return;
    setBusy(true);
    setError(null);
    try {
      await revertCopyOverride({
        key: meta.key, scopeType: scope.type, scopeId: scope.id,
        expectedVersion: resolution.override.version,
      });
      load();
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not revert.');
    } finally {
      setBusy(false);
    }
  }

  const tooLong = draft.trim().length > meta.maxLength;
  const isBlank = draft.trim() === '' && !meta.allowEmpty;
  const unchanged = resolution?.override?.value === draft.trim();

  return (
    <section className="net-panel cpy-editor">
      <div className="spc-head">
        <div>
          <h2 className="net-panel__title mono">{meta.key}</h2>
          <p className="net-panel__lede">{meta.description}</p>
        </div>
        <div className="net-row__badges">
          <span className="badge badge--muted">{meta.group}</span>
          <span className="badge badge--muted">max {meta.maxLength}</span>
          {meta.variables.length > 0 && (
            <span className="badge badge--network">
              {meta.variables.map((v) => `{${v}}`).join(' ')}
            </span>
          )}
        </div>
      </div>

      <ScopePicker
        supported={meta.supportedScopes}
        value={scope}
        onChange={onScopeChange}
        orgs={orgs}
        venues={venues}
        events={events}
      />

      {!scopeReady && (
        <p className="adm-lede">Choose a {scope.type} to edit its wording.</p>
      )}

      {error && <p className="alert alert--error" role="alert">{error}</p>}

      {resolution && (
        <>
          {/* Default / Override / Effective, stated separately and labelled. */}
          <dl className="cpy-values">
            <div>
              <dt>Code default</dt>
              <dd className="cpy-values__default">{resolution.codeDefault}</dd>
            </div>
            <div>
              <dt>Override at this {scope.type}</dt>
              <dd>
                {resolution.override
                  ? <span className="cpy-values__override">{resolution.override.value}</span>
                  : <span className="cpy-values__none">none</span>}
              </dd>
            </div>
            <div>
              <dt>Effective here</dt>
              <dd>
                <span className="cpy-values__effective">{resolution.effective}</span>
                <span className="badge badge--muted cpy-values__from">
                  from {resolution.effectiveFrom}
                </span>
              </dd>
            </div>
          </dl>

          <label className="field">
            <span className="field__label">New wording</span>
            <textarea
              className="field__input cpy-textarea"
              rows={meta.maxLength > 120 ? 4 : 2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          </label>
          <p className={`cpy-count${tooLong ? ' is-over' : ''}`}>
            {draft.trim().length} / {meta.maxLength}
            {meta.variables.length > 0 && ` · placeholders: ${meta.variables.map((v) => `{${v}}`).join(', ')}`}
          </p>

          <div className="spc-actions">
            <button
              type="button"
              className="button button--primary"
              disabled={busy || tooLong || isBlank || unchanged}
              onClick={() => void save()}
            >
              Save to {scope.type}
            </button>
            <button
              type="button"
              className="button button--ghost"
              disabled={busy || !resolution.override}
              onClick={() => void revert()}
            >
              Revert to default
            </button>
            <button
              type="button"
              className="button button--ghost adm-btn-sm"
              aria-expanded={showHistory}
              onClick={() => setShowHistory((v) => !v)}
            >
              {showHistory ? 'Hide history' : `History (${history.length})`}
            </button>
          </div>

          {isBlank && (
            <p className="adm-lede">
              Blank is not an override. Use Revert to remove wording, so an empty box never
              silently hides part of the page.
            </p>
          )}

          {showHistory && <History rows={history} />}
        </>
      )}
    </section>
  );
}

export function AdminCopy() {
  const { organizationId } = useAdminScope();
  const [searchParams] = useSearchParams();
  // A deep link from somewhere that knows which string it means — the CMS inspector's
  // "Edit in Copy" beside a block whose words come from here. The key is a filter and a
  // selection, never a grant: every read and write is still authorized by the engine.
  const linkedKey = searchParams.get('key');

  const [keys, setKeys] = useState<CopyKey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(linkedKey ?? '');
  const [group, setGroup] = useState('');
  const [selected, setSelected] = useState<string | null>(linkedKey);
  const [scope, setScope] = useState<ScopeTarget>({ type: 'platform', id: null });
  const [reloadToken, setReloadToken] = useState(0);

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [venues, setVenues] = useState<DirectoryVenue[]>([]);
  const [events, setEvents] = useState<ComplianceEvent[]>([]);

  usePageChrome({
    title: 'Copy',
    subtitle: 'Presentation wording, editable without a deploy. Words only: nothing here '
      + 'changes prices, permissions or what the product does.',
  });

  useEffect(() => {
    listCopyKeys()
      .then(setKeys)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load the copy registry.'));
    listOrganizations().then(setOrgs).catch(() => setOrgs([]));
    listVenueDirectory().then(setVenues).catch(() => setVenues([]));
    listCompliance('all').then(setEvents).catch(() => setEvents([]));
  }, []);

  // Once the registry is loaded, apply the deep link. A key that no longer exists leaves
  // the search term in the box and no selection, which reads as "not found" rather than
  // as an empty screen.
  useEffect(() => {
    if (!linkedKey || !keys) return;
    const match = keys.find((k) => k.key === linkedKey);
    if (match) select(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedKey, keys]);

  const groups = useMemo(
    () => [...new Set((keys ?? []).map((k) => k.group))].sort(),
    [keys],
  );

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (keys ?? []).filter((k) => {
      if (group && k.group !== group) return false;
      if (!term) return true;
      // Key, description, group and the shipped wording — the four things somebody
      // actually remembers about a string they want to change.
      return k.key.toLowerCase().includes(term)
        || k.description.toLowerCase().includes(term)
        || k.group.toLowerCase().includes(term)
        || k.codeDefault.toLowerCase().includes(term);
    });
  }, [keys, search, group]);

  const meta = (keys ?? []).find((k) => k.key === selected) ?? null;

  function select(next: CopyKey) {
    setSelected(next.key);
    // Seed from the Admin header context ONLY when the key supports it, and only as a
    // starting point the picker then shows explicitly.
    if (organizationId && next.supportedScopes.includes('organization')) {
      setScope({ type: 'organization', id: organizationId });
    } else {
      setScope({ type: 'platform', id: null });
    }
  }

  return (
    <div className="adm-page">
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <div className="adm-filters">
        <input
          className="adm-select cpy-search"
          type="search"
          placeholder="Search keys, descriptions or wording…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search copy keys"
        />
        <select className="adm-select" value={group} onChange={(e) => setGroup(e.target.value)}
          aria-label="Filter by group">
          <option value="">All groups</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      {!keys && !error && <p className="adm-lede">Loading the copy registry…</p>}
      {keys && shown.length === 0 && <p className="adm-lede">No keys match that search.</p>}

      <div className="cpy-layout">
        <ul className="cpy-list">
          {shown.map((k) => (
            <li key={k.key}>
              <button
                type="button"
                className={`cpy-list__item${selected === k.key ? ' is-active' : ''}`}
                aria-current={selected === k.key ? 'true' : undefined}
                onClick={() => select(k)}
              >
                <span className="cpy-list__key mono">{k.key}</span>
                <span className="cpy-list__default">{k.codeDefault}</span>
                <span className="badge badge--muted">{k.group}</span>
              </button>
            </li>
          ))}
        </ul>

        <div className="cpy-detail">
          {meta ? (
            <Editor
              key={`${meta.key}:${reloadToken}`}
              meta={meta}
              scope={scope}
              onScopeChange={setScope}
              orgs={orgs}
              venues={venues}
              events={events}
              onSaved={() => setReloadToken((n) => n + 1)}
            />
          ) : (
            <p className="adm-lede">Pick a key to see its default, its override and what renders.</p>
          )}
        </div>
      </div>
    </div>
  );
}
